<script lang="ts" context="module">
  export let isLowResolutionPreview = writable(false);
  interface RenderedVideo {
    type: string;
    file: {
      publicUrl: string;
    };
  }
  export function selectBestRenderedVideo(
    videos: any[],
    usesAICameraCrew: boolean,
    isV2: boolean = false
  ): RenderedVideo | null {
    let selected = null;

    if (usesAICameraCrew) {
      if (isV2) {
        selected = videos.find(video => video.type == 'aiccv2');
      } else {
        selected = videos.find(video => video.type == 'aicc');
        if (!selected) {
          // Fallback to v2 if v1 is not found
          selected = videos.find(video => video.type == 'aiccv2');
        }
        if (!selected) {
          // fallback to final if v2 is not found
          selected = videos.find(video => video.type == 'final');
        }
      }
    }

    if (!selected) {
      // Find final or low in videos
      for (let i = 0; i < videos.length; i++) {
        const rendered = videos[i];
        if (rendered.type == 'low' || rendered.type == 'final') selected = rendered;
        if (selected && selected.type == 'final') return selected;
      }
    }

    return selected;
  }
</script>

<script lang="ts">
  import {v4} from 'uuid';
  import {onDestroy, onMount} from 'svelte';
  import type {Video} from '@/types/project';
  import {preview, project, user, download, step, ProjectState, draftStore, animation} from '@/store';
  import {Icon, PreviewStyles} from '@components';
  import {getInstance} from '@/services/animation';
  import {VideoService, CompilationService, appEvents} from '@/services';
  import {toTitleCase, wait} from '../../utils/common';
  import type {TemplateInformation} from '../../types/preview';
  import previewAreaLoadingStore from './loading-store';
  import editorInstance from '../../lib/editor/editor';
  import Swal from 'sweetalert2';
  import {STEP} from '../../types/common';
  import appConfig from '../../config';
  import editorStore from '../../lib/editor/editor.store';
  import PopupBox from '../../components/new-preview-sidebar/PopupBox/PopupBox.svelte';
  import {videoScale} from '@/store/scale';
  import {logError} from '../../utils/logs';
  import AnimationController, {animationPlayerInstance} from '../../components/animations/AnimationController.svelte';
  import DraftService from '../../services/drafts';
  import {AnimationPlayerState} from '../../services/animation/timeline-player/animation-timeline-player';
  import {writable} from 'svelte/store';
  import tippy from 'tippy.js';
  import {topnav} from '../../utils/top-nav-next-custom-events';
  import {finalDownloadBtnStore} from '../../platform-redesign-components/DownloadPreviewButton.svelte';
  import LowResolutionNotice from './LowResolutionNotice.svelte';
  import statisticsService, {Statistic, StatisticsType} from '../../statistics';
  import {validateTemplateIsSermonclipTemplate} from '../../utils/validator';

  // export let zoomPercent: number;
  let container: HTMLElement;
  let animationService = getInstance();
  const videoService = new VideoService();
  const compilationService = new CompilationService();
  let video: Video;
  let loadingProgress = 0;
  let lastKnownTime = 0;
  let status = 'Compiling video... this typically takes less than a minute';
  let totalTime = $preview.totalDuration;
  let trackChanges = false;
  let scale = 1;

  let lowResStat: Statistic;
  let finalCompileStat: Statistic;

  const displayNetworkError = () => {
    Swal.fire({
      title: 'There appears to have been a problem.',
      text: "The system was unable to compile your video. This is usually because the source video is too large or the quality is too high. We are working on solutions to mitigate the issue. In the mean time, if you are able to upload a smaller or lower quality video, that may help. If the issue persists or you don't believe this to be the case, please contact customer support immediately and our team will be ready to assist you.",
      icon: 'error'
    });
  };

  const delay = async (time: number) => {
    return new Promise(resolve => setTimeout(resolve, time));
  };

  const loadVideo = async (tries: number) => {
    if (tries == 5) {
      displayNetworkError();
      throw new Error('Could not load video.');
    }
    try {
      if (!$project.video) throw new Error('Project Video is undefined.');
      video = await videoService.getVideo($project.video.id);
    } catch (error) {
      logError('Unable to load video', error);
      await delay(1000);
      loadVideo(tries + 1);
    }
  };
  let mountEvents: any = [];
  onMount(async () => {
    topnav.dispatchEvent({
      previousDisabled: true,
      previousDisabledTip: 'Video rendering in progress...'
    });
    finalDownloadBtnStore.set({
      disabled: true,
      disabledTooltip: 'Video rendering in progress...'
    });
    mountEvents = [];
    await loadVideo(1);
    const template = $project.template;
    console.log('selected template', template);
    if (template && template.content) {
      const valid = validateTemplateIsSermonclipTemplate(template.content);
      if (!valid) {
        console.warn('Template is not a sermonclip template');

        alert(
          'We sincerely apologize for the inconvenience. There seems to have been an issue with the template data selected for this project. Please reselect a template and try again...'
        );
        project.selectTemplate(null);
        project.updated();
        step.update(STEP.CHOOSE_TEMPLATE);

        return false;
      }
    }

    if (!template) throw 'Undefined Video/Template';

    const identifier = v4();
    let compileId: number | null;

    // There is a fallback that checks every 5 seconds if the video has been compiled. This is incase the server sent event fails from network issues

    const draftService = new DraftService();

    if ($project.isNew || $draftStore.descriptions == null) {
      // Request to Generate Social Descriptions;
      try {
        draftStore.generateSocialDescriptions($draftStore.currentCloudDraftId as number, true as boolean);
      } catch (error) {
        console.log(error);
      }
    }

    if (
      appConfig.editorSkip ||
      ($project.isNew == false && $project.isUpdated == false && $project.compiledVideo != null)
    ) {
      topnav.dispatchEvent({
        previousDisabled: false,
        previousDisabledTip: ''
      });
      finalDownloadBtnStore.set({
        disabled: false,
        disabledTooltip: ''
      });
      // console.log("Loading project...");
      // createPreviewDom(template, $project);
      const draft = await draftService.getDraft($draftStore.currentCloudDraftId as number);
      // console.log("fetched draft", draft.data);

      // This if statement exists so that all legacy projects can still be rendered. previously there wasnt any "renderedVideos" in the draft.data
      if (draft.data.renderedVideos.length > 0) createPreviewdomFromDraft(template, $project, draft.data);
      else createPreviewDom(template, $project);
    } else {
      console.log('Compiling video...');
      compilationService.addVideo(video).addSubtitles($project.selectedSentences);
      try {
        lowResStat = await statisticsService.create(StatisticsType.COMPILE_LOW_QUALITY_RENDER);
        finalCompileStat = await statisticsService.create(StatisticsType.COMPILE_FULL_RENDER);
        statisticsService.save(lowResStat, {type: 'start', timestamp: new Date().toISOString()});
        statisticsService.save(finalCompileStat, {type: 'start', timestamp: new Date().toISOString()});
      } catch (err) {
        console.error('Error creating statistic', err);
      }
      try {
        download.set({
          isComplete: false,
          progress: 0
        });

        const compileStatus = await compilationService.prepare(identifier, $draftStore);
        // console.log('compileStatus', compileStatus);
        compileId = compileStatus.id;
        // let intervalCheck = setInterval(fallbackCheckStatusCompleted, 5000);
        async function fallbackCheckStatusCompleted(): Promise<any> {
          // console.log('Executing fallback status check in 3000ms');
          setTimeout(async () => {
            if (!compileId) return console.error('[fallbackCheckStatusCompleted] Compile ID');
            // console.log('checking status', compileId);
            const resp = await compilationService.checkStatus(compileId);
            // console.log(resp);
            if (resp.status == 'done') {
              fetchVideo(resp.video.id);
            } else if (resp.status == 'failed') {
              if ($step == STEP.PREVIEW)
                Swal.fire('There was an error compiling your video. Please try again later.', '', 'error');
            } else {
              fallbackCheckStatusCompleted();
            }
          }, 3000);
        }
        // fallbackCheckStatusCompleted();
        previewAreaLoadingStore.set(true);
      } catch (err) {
        logError('[Compilation step error]', err);
        Swal.fire({
          title: 'There appears to have been a problem.',
          text: 'The development team has been alerted that there may have been an issue. In the mean time, please try again. but if the issue continues to persist, please try and contact customer support',
          icon: 'error'
        });
        step.update(STEP.SELECT_SUBTITLES);
      }
    }

    let fetched = false;

    function fetchVideo(id: number) {
      console.trace('fetchVideo', id);
      if (fetched) return;
      fetched = true;
      const videoService = new VideoService();
      videoService.getVideo(id, false).then(video => {
        // console.log('compiledVideo', video);
        project.set({
          compiledVideo: video.compiled,
          isUpdated: false,
          isNew: false
        });
        // console.log("Project compiled video is:", video.compiled);
        createPreviewDom(template, $project);
      });
    }

    // This is called whent he final video is rendered.

    async function updateAttachedVideo(url: string) {
      isLowResolutionPreview.set(false);

      topnav.dispatchEvent({
        previousDisabled: false,
        previousDisabledTip: '',
        nextDisabled: false,
        nextDisabledTip: ''
      });
      finalDownloadBtnStore.set({
        disabled: false,
        disabledTooltip: ''
      });

      const state = animationPlayerInstance.animationPlayerState;
      animationPlayerInstance.pause();
      await animationService.attachVideoObject(url, true);
      if (state == AnimationPlayerState.PLAYING || state == AnimationPlayerState.BUFFERING)
        animationPlayerInstance.play();
    }

    const onVideoRenderedEvent = async (data: any) => {
      // console.log("The low resolution video has been rendered", data);
      const draft = await draftService.getDraft($draftStore.currentCloudDraftId as number);

      // console.log("fetched draft", draft);
      // This function assumes that rendering will always be highest quality - last so everytime we receive a video rendered event that means that it is a higher quality video. Note: this can change in the future
      project.set({
        compiledVideo: data.renderedVideo.file,
        isUpdated: false,
        isNew: false
      });
      if (!renderStatus) createPreviewdomFromDraft(template, $project, draft.data);
      else updateAttachedVideo(data.renderedVideo.file.publicUrl);
    };
    const renderedEventSubscription = appEvents.on('rendered-video.low-resolution-video-rendered', e => {
      console.log('Low resolution video rendered', e);
      if (lowResStat) {
        try {
          statisticsService.save(lowResStat, {type: 'end', timestamp: new Date().toISOString()});
        } catch (err) {
          console.error('Error saving statistic', err);
        }
      }
      onVideoRenderedEvent(e);
    });

    const finalRenderEventSubscription = appEvents.on('rendered-video.final-video-rendered', e => {
      console.log('Final video rendered', e);
      if (finalCompileStat) {
        try {
          statisticsService.save(finalCompileStat, {type: 'end', timestamp: new Date().toISOString()});
        } catch (err) {
          console.error('Error saving statistic', err);
        }
      }
      onVideoRenderedEvent(e);
    });

    const cutVideoEventSubscription = appEvents.on('cut-video', async data => {
      if (data.status == 'error') {
        logError('[Compilation step error]', data);
        displayNetworkError();
        step.update(STEP.SELECT_SUBTITLES);
      }
      return; // Do nothing for now.
      if (!(data.identifier == identifier)) return false;
      console.log('app broadcasted event cut-video', data);
      if (data.status == 'completed') {
        if (data.identifier == identifier) {
          fetchVideo(data.video);
        } else {
          logError('Failed to Fetch', data);
          status = 'Processing video';
          return false;
        }
      }

      if (data.status == 'processing') {
        if (!(data.identifier == identifier)) return false;

        if (data.progress.duration) {
          totalTime = data.progress.duration;
          status = 'Processing video';
        }

        if (data.progress && data.progress.time) {
          const processtime = data.progress.time * 100;
          if (processtime > lastKnownTime) {
            lastKnownTime = processtime;
            const computedProgress = (lastKnownTime / totalTime) * 100;
            if (computedProgress <= 100) loadingProgress = computedProgress;
          }
        }
      }

      if (data.status == 'error') {
        logError('[Compilation step error]', data);
        displayNetworkError();
        step.update(STEP.SELECT_SUBTITLES);
      }
    });

    mountEvents.push(renderedEventSubscription);
    mountEvents.push(finalRenderEventSubscription);
    mountEvents.push(cutVideoEventSubscription);
  });

  let sermonclipHeight = 0;
  let sermonclipWidth = 0;
  let sermonclipScale = 0;
  let containerScale = 1;

  let renderStatus = false;
  const createPreviewdomFromDraft = async (template: any, projectStore: ProjectState, draft: any) => {
    console.log('createPreviewdomFromDraft', template, draft);
    const node = animationService.getDom();
    node.style.opacity = '1';
    if (!container)
      return console.error('CONTAINER ELEMENT NOT FOUND', {
        container,
        node,
        template,
        project: projectStore
      });

    container.innerHTML = '';
    container.append(node);
    node.addEventListener('click', e => {
      const target: HTMLElement = e.target as HTMLElement;
      if (!(target === node)) return;
      editorInstance.disableFocusMode();
      editorStore.setActiveComponent(null);
    });

    animationService.reset().onCompleteTemplateBuild(() => {
      previewAreaLoadingStore.set(false);
    });

    await animationService.buildTemplate(template.content);
    animationService.recomputeCaptionTextSize();
    const usesAICameraCrew = projectStore.usesAICameraCrew || projectStore.usesAICameraCrewV2 ? true : false;

    const renderedVideos = draft.renderedVideos;
    const render = selectBestRenderedVideo(renderedVideos, usesAICameraCrew, projectStore.usesAICameraCrewV2);
    console.log('Selecting best render', renderedVideos, usesAICameraCrew, projectStore.usesAICameraCrewV2);
    if (!render) throw new Error('Render Unavailable');
    if (render.type == 'low') {
      isLowResolutionPreview.set(true);
      topnav.dispatchEvent({
        nextDisabled: true,
        nextDisabledTip: 'Please wait for the final video to be rendered before downloading...',
        previousDisabled: true,
        previousDisabledTip:
          'You need to wait for the final video to be rendered before you can move back to the previous step.'
      });
      finalDownloadBtnStore.set({
        disabled: true,
        disabledTooltip: 'Please wait for the final video to be rendered before downloading...'
      });
    } else {
      isLowResolutionPreview.set(false);
      finalDownloadBtnStore.set({
        disabled: false,
        disabledTooltip: ''
      });
      topnav.dispatchEvent({
        previousDisabled: false,
        previousDisabledTip: ''
      });
    }
    await animationService.attachVideoObject(render.file.publicUrl);
    await wait(1000);
    try {
      animationService.setTotalDuration(projectStore.totalDuration);
    } catch (err) {
      console.warn('[Could not set total duration]', err);
    }

    const textAnimation = $preview.textAnimation;

    animationService.buildTranscriptionAnimationData(video.transcription, projectStore.selectedSentences, video.delays);
    await animationService.buildAnimations(true, () => {
      trackChanges = true;
      node.style.opacity = '1';
      editorInstance.initialize(animationService.getDom());
    });
    animationService.templateLoader.initializeLoaders();
    const ignored = ['sermonclip', 'caption', 'logo', 'sermon title'];

    let extraInformation: Array<TemplateInformation> = [];
    renderStatus = true;
    node.querySelectorAll('[data-amelement]').forEach(element => {
      const attribute = element.getAttribute('data-amelement') as string;

      if (attribute === 'sermonclip') {
        const div = element as HTMLDivElement;

        sermonclipHeight = div.clientHeight;
        sermonclipWidth = div.clientWidth;

        videoScale.set({
          videoHeight: sermonclipHeight,
          videoWidth: sermonclipWidth,
          scale: sermonclipScale
        });
      }

      if (attribute && ignored.includes(attribute)) return false; // Skip this ignored case
      const type = element.getAttribute('data-amtype') as string;
      const value = element.textContent?.replace(/[\n\r]+|[\s]{2,}/g, ' ').trim() || ('' as string);
      extraInformation.push({
        key: attribute,
        label: toTitleCase(attribute),
        value,
        type
      });
    });

    animationService.rebuildChanges();
    animationService.restartTimeline();
    project.set({
      compiledVideo: render.file,
      isUpdated: false,
      isNew: false
    });
    preview.set({
      totalDuration: projectStore.totalDuration || animationService.duration,
      checkIfSermonTitleExist: animationService.checkForSermonTitle(),
      checkIfBrandLogoExists: animationService.checkForBrandLogo(),
      music: animationService.templateMusic,
      extraInformation
    });
  };

  const createPreviewDom = async (template: any, project: ProjectState) => {
    topnav.dispatchEvent({
      previousDisabled: false,
      previousDisabledTip: '',
      nextDisabled: false,
      nextDisabledTip: ''
    });
    finalDownloadBtnStore.set({
      disabled: false,
      disabledTooltip: ''
    });
    const node = animationService.getDom();
    node.style.opacity = '0';
    if (!container)
      return console.error('CONTAINER ELEMENT NOT FOUND', {
        container,
        node,
        template,
        project
      });

    container.innerHTML = '';
    container.append(node);
    node.addEventListener('click', e => {
      const target: HTMLElement = e.target as HTMLElement;
      if (!(target === node)) return;
      editorInstance.disableFocusMode();
      editorStore.setActiveComponent(null);
    });
    animationService.reset().onCompleteTemplateBuild(() => {
      previewAreaLoadingStore.set(false);
    });

    console.log('Building template...', template, template.content);
    await animationService.buildTemplate(template.content);

    try {
      let url =
        project.compiledAICameraCrewVideo && project.usesAICameraCrew
          ? project.compiledAICameraCrewVideo.publicUrl
          : project.compiledVideo?.publicUrl || '';

      if (url === '') throw 'URL is empty.';

      await animationService.attachVideoObject(url);
    } catch (error) {
      console.error('createPreviewDOM', error);
      Swal.fire({
        title: 'There appears to have been a problem.',
        text: "While trying to load the video, a network problem occurs that prevents us from loading the video. The development team has been alerted and we'll be investigating this. In the mean time, please try again at a later time. Your current progress is saved but if the issue continues to persist, please try and contact customer support",
        icon: 'error'
      });
    }
    await wait(1000);
    try {
      animationService.setTotalDuration(project.totalDuration);
    } catch (err) {
      console.warn('[createPreviewDom] Could not set the total duration', err);
    }

    const textAnimation = $preview.textAnimation;
    animationService.buildTranscriptionAnimationData(video.transcription, project.selectedSentences, video.delays);
    await animationService.buildAnimations(true, () => {
      trackChanges = true;
      node.style.opacity = '1';
      editorInstance.initialize(animationService.getDom());
    });
    animationService.templateLoader.initializeLoaders();
    const ignored = ['sermonclip', 'caption', 'logo', 'sermon title'];

    let extraInformation: Array<TemplateInformation> = [];

    node.querySelectorAll('[data-amelement]').forEach(element => {
      const attribute = element.getAttribute('data-amelement') as string;

      if (attribute === 'sermonclip') {
        const div = element as HTMLDivElement;

        sermonclipHeight = div.clientHeight;
        sermonclipWidth = div.clientWidth;

        videoScale.set({
          videoHeight: sermonclipHeight,
          videoWidth: sermonclipWidth,
          scale: sermonclipScale
        });
      }

      if (attribute && ignored.includes(attribute)) return false; // Skip this ignored case
      const type = element.getAttribute('data-amtype') as string;
      const value = element.textContent?.replace(/[\n\r]+|[\s]{2,}/g, ' ').trim() || ('' as string);
      extraInformation.push({
        key: attribute,
        label: toTitleCase(attribute),
        value,
        type
      });
    });

    animationService.rebuildChanges();
    animationService.restartTimeline();

    preview.set({
      totalDuration: project.totalDuration || animationService.duration,
      checkIfSermonTitleExist: animationService.checkForSermonTitle(),
      checkIfBrandLogoExists: animationService.checkForBrandLogo(),
      music: animationService.templateMusic,
      extraInformation
    });
  };

  const unsubscribe = preview.subscribe(async store => {
    animationService.setMetaData({...store, brandKit: $user.brandKit});
    if (!trackChanges) return;
    // animationService.pause();
    // animationService.restartTimeline(true);
    // animationService.buildMetadataDisplay();
  });

  $: loadingText = loadingProgress > 0 ? loadingProgress.toFixed(0) + '%' : status;

  onDestroy(() => {
    console.warn('Unloading editor instance...');
    editorInstance.unload();
    unsubscribe();
    if (mountEvents && mountEvents.length) {
      mountEvents.forEach((event: any) => {
        event();
      });
    }
  });

  function lowResolutiontooltip(node: any) {
    const instance = tippy(node, {
      content:
        'We are currently rendering the final version of your video, what you are currently previewing is a low resolution version of your video. This is to give you an idea of what the final video will look like. The final video will be available shortly.'
    });
    return {
      destroy() {
        try {
          //@ts-ignore
          instance.destroy();
        } catch (err) {
          console.warn('Problem when destroying the tippy instance');
        }
      }
    };
  }

  $: isEditorLoadingClass = $editorStore.initialized ? '' : 'pointer-events-none opacity-20';
</script>

<div class="relative flex w-full flex-grow flex-col overflow-auto">
  <AnimationController />

  {#if $isLowResolutionPreview}
    <LowResolutionNotice />
  {/if}
  <div class="my-auto block flex-grow overflow-hidden p-5">
    <div class="my-auto h-full flex-grow" style="transform: scale({containerScale * 100}%);">
      <div id="containerArea" class="my-auto h-full flex-grow {isEditorLoadingClass}" bind:this={container}>
        {#if previewAreaLoadingStore}
          <div class="flex h-full w-full flex-col items-center justify-center bg-gray-100 text-center align-middle">
            <Icon name="Spinner" className=" text-gray-400 w-12 h-12 animate-spin" />
            <div class="mt-2 block space-y-4 text-sm text-gray-500">
              <p>{loadingText}</p>
              <p>
                If compiling takes more than 5 minutes, please consider re-uploading your video from a file and turn on
                the Resync option
              </p>
            </div>
          </div>
        {/if}
      </div>
    </div>
  </div>
</div>

<PreviewStyles />

<style lang="postcss">
  div#containerArea {
    /* position: absolute; */

    width: 100%;
    /* height: 80%; */
  }

  div#containerArea > div > div {
    @apply p-4;
  }
  /* input {
        -webkit-appearance: none;
        background-size: 0% 100%;
        background-repeat: no-repeat;
        @apply w-full h-1 bg-gray-300 bg-gradient-to-r from-secondary to-secondary-600 rounded;
    

    input::-webkit-slider-thumb {
        -webkit-appearance: none;
        @apply bg-secondary w-4 h-4 rounded-full shadow-md;
    }

    input::-webkit-slider-runnable-track {
        -webkit-appearance: none;
        @apply bg-transparent;
    } */
</style>
